import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Col, Row } from "antd";
import { filter, keys, map, reduce, uniq, includes, each } from "underscore";
import {
    calculateChangePerc,
    formatCrossChannelsTrendData,
    formatCustomerSatisfactionData,
    formatDriverwiseReviewTrendData,
    formatProductRatingVsCompetitorsSpiderChart,
    formatProductRatingVsCompetitorsTrend,
    formatSatisfactionDriversData,
    formatSatisfactionDriversTrendData,
    getTop5Sentiments,
    groupByDateDriverwiseReviewTrendData,
} from "@ts/widget";
import {
    SummaryWidget,
    SatisfactionDriversSummaryWidget,
    CsAndReviewTrendGroupedWidget,
    TrendsWithCompareModalWidget,
    WordCloudsWidget,
    ProductRatingVsCompetitorsWidget,
    AllReviewsWidget,
    SatisfactionDriversWidget,
    CrossChannelRatingAndReviewWidget,
} from "@components/widgets";
import {
    IProductAnalysisRatingsAndReviewsModule,
    ProductDeepdiveModules,
    SentimentType,
    TReviewDrawerTitleAction,
} from "@ts/common-types";
import { reviewDrawerTitleInitialState } from "@ts/constants";
import { startCase, titleCase } from "@ts/utils";
import WidgetModuleLayout from "./WidgetModuleLayout";
import { useCrossChannelTableData } from "@hooks/AllCustomHooks";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";

const reviewDrawerTitleReducer = (
    state: any,
    action: TReviewDrawerTitleAction
) => {
    switch (action.type) {
        case "all-review":
            return { ...state, "all-review": action.payload };
        case "satisfaction-driver":
            return { ...state, "satisfaction-driver": action.payload };
        case "cross-channel":
            return { ...state, "cross-channel": action.payload };
        default:
            throw new Error(`Unhandled action type: ${(action as any).type}`);
    }
};

const ProductAnalysisRatingsAndReviewsModule: React.FC<
    IProductAnalysisRatingsAndReviewsModule
> = ({ loadingState, selectedSKU, reviewsData, channel }) => {
    const [showAllReviewsWidget, setShowAllReviewsWidget] =
        useState<boolean>(false);
    const [showSatisfactionDriversWidget, setShowSatisfactionDriversWidget] =
        useState<boolean>(false);
    const [selectedDriver, setSelectedDriver] = useState([""]);
    const [
        showCrossChannelRatingAndReviewWidget,
        setShowCrossChannelRatingAndReviewWidget,
    ] = useState(false);
    const [activeReviewTab, setActiveReviewTab] = useState<string>("all");
    const [activeDriversTab, setActiveDriversTab] = useState<string>("all");
    const [selectedFeature, setSelectedFeature] = useState<string[]>([]);
    const [selectedRatingFilter, setSelectedRatingFilter] = useState<string[]>([
        "all",
    ]);
    const [selectedComparisionChannel, setSelectedComparisionChannel] =
        useState<string[]>(["all"]);

    const [comparisionSelectDataType, setComparisionSelectDataType] =
        useState<any>("count");
    const [reviewDrawerTitleState, setReviewDrawerTitleState] = useReducer(
        reviewDrawerTitleReducer,
        reviewDrawerTitleInitialState
    );

    const allReviewWidgetData = useMemo(() => {
        const allReviewData = reviewsData?.allReviewData;
        let table = allReviewData?.table;
        if (table && keys(table).length) {
            const { tableData, expandedTableData } = table;
            const filteredTableData =
                activeReviewTab === "all"
                    ? tableData
                    : filter(
                          tableData,
                          (data: any) => data.sentiment_type === activeReviewTab
                      );
            table = map(filteredTableData, (td) => {
                const currentReviewData =
                    activeReviewTab === "all"
                        ? expandedTableData[td.review_id]
                        : filter(
                              expandedTableData[td.review_id],
                              (data: any) =>
                                  data.sentiment_type === activeReviewTab
                          );
                const aboutDriver = map(currentReviewData, (cd) => ({
                    name: cd.theme,
                    type: cd.sentiment_type,
                }));
                return {
                    ...td,
                    key: td.review_id,
                    about_driver: aboutDriver,
                    details: currentReviewData,
                };
            });
        }
        return {
            ...allReviewData,
            table,
        };
    }, [activeReviewTab, reviewsData]);

    const satisfactionDriverWidgetData = useMemo(() => {
        const satisfactionDriversPopup = filter(
            reviewsData?.satisfactionDriversPopup,
            (driverData) =>
                (includes(selectedDriver, "all") ||
                    includes(selectedDriver, driverData.sentiment)) &&
                (activeDriversTab === driverData.sentiment_type ||
                    activeDriversTab === "all")
        );
        return {
            chart: formatSatisfactionDriversTrendData(satisfactionDriversPopup),
            table: formatSatisfactionDriversData(satisfactionDriversPopup),
        };
    }, [reviewsData, selectedDriver, activeDriversTab]);

    const driverFilterOptions = useMemo(() => {
        if (reviewsData?.satisfactionDriversPopup?.length === 0) return [];
        const distinctDrivers = uniq(
            reviewsData.satisfactionDriversPopup,
            (driver: any) => driver.sentiment
        );

        return [
            {
                label: "All",
                value: "all",
                disabled:
                    selectedDriver.length > 1 &&
                    !includes(selectedDriver, "all"),
            },
            ...map(distinctDrivers, (driver: any) => ({
                label: startCase(driver.sentiment),
                value: driver.sentiment,
                disabled: selectedDriver.includes("all"),
            })),
        ];
    }, [reviewsData, selectedDriver]);

    const comparisionSelectOptions = useMemo(() => {
        if (reviewsData?.crossChannelTrends?.reviews?.length === 0) return [];
        const distinctChannels = uniq(
            filter(
                reviewsData?.crossChannelTrends?.ratings,
                (channelData) =>
                    channelData["channel_id"] &&
                    channelData["channel_id"] !== channel
            ),
            (channelData) => channelData["channel_id"]
        );

        return [
            {
                label: "All",
                value: "all",
                disabled:
                    selectedComparisionChannel.length > 1 &&
                    !includes(selectedComparisionChannel, "all"),
            },
            ...map(distinctChannels, (channelData: any) => ({
                label: titleCase(
                    CHANNEL_CONFIGS[channelData["channel_id"]].name
                ),
                value: channelData["channel_id"],
                disabled: selectedComparisionChannel.includes("all"),
            })),
        ];
    }, [reviewsData, selectedComparisionChannel, channel]);

    const productRatingVsCompetitorsData = useMemo(() => {
        const competitorsData = reviewsData?.productRatingVsCompetitors;
        if (
            !competitorsData ||
            (!competitorsData.productRatings?.length &&
                !competitorsData.productFeatureRatings?.length)
        ) {
            return {
                trendChart: {},
                spiderChart: {},
            };
        }

        const { productRatings = [], productFeatureRatings = [] } =
            competitorsData;
        return {
            trendChart: formatProductRatingVsCompetitorsTrend(
                includes(selectedRatingFilter, "all")
                    ? productRatings
                    : filter(
                          productRatings,
                          (data) =>
                              data.sku === selectedSKU ||
                              includes(selectedRatingFilter, data.sku)
                      ),
                selectedSKU
            ),
            spiderChart: formatProductRatingVsCompetitorsSpiderChart(
                includes(selectedRatingFilter, "all")
                    ? productFeatureRatings
                    : filter(
                          productFeatureRatings,
                          (data) =>
                              data.sku === selectedSKU ||
                              includes(selectedRatingFilter, data.sku)
                      ),
                selectedSKU
            ),
        };
    }, [reviewsData, selectedSKU, selectedRatingFilter]);

    const productRatingVsCompetitorsFilterOptions = useMemo(() => {
        if (
            reviewsData?.productRatingVsCompetitors?.productRatings?.length ===
            0
        )
            return [];
        return [
            {
                label: "All",
                value: "all",
                disabled:
                    selectedRatingFilter.length > 1 &&
                    !includes(selectedRatingFilter, "all"),
            },
            ...map(
                filter(
                    reviewsData?.productRatingVsCompetitors?.productRatings,
                    (data) => data.sku !== selectedSKU
                ),
                (product: any) => ({
                    label: product.sku,
                    value: product.sku,
                    disabled: selectedRatingFilter.includes("all"),
                })
            ),
        ];
    }, [reviewsData, selectedSKU, selectedRatingFilter]);

    const crossChannelTableData = useCrossChannelTableData(
        reviewsData?.crossChannelData,
        reviewsData?.crossChannelDataWithFeatures
    );

    const crossChannelWidgetData = useMemo(() => {
        const { tableData, expandedTableData } = crossChannelTableData;

        const formatTrendData = (
            dataKey: keyof typeof reviewsData.crossChannelTrends
        ) => {
            return formatCrossChannelsTrendData(
                reviewsData?.crossChannelTrends?.[dataKey],
                comparisionSelectDataType,
                selectedComparisionChannel
            );
        };

        return {
            charts: {
                reviews: {
                    count: formatTrendData("reviews_count"),
                    score: formatTrendData("reviews_score"),
                },
                ratings: {
                    count: formatTrendData("ratings_count"),
                    score: formatTrendData("ratings_score"),
                },
            },
            tableData,
            expandedTableData,
        };
    }, [
        reviewsData,
        comparisionSelectDataType,
        selectedComparisionChannel,
        crossChannelTableData,
    ]);

    const reviewsDataFormatted = useMemo(() => {
        const { customerSatisfactionDriver, driverwiseReviewTrend } =
            reviewsData;
        const filteredCsDrivers = filter(
            customerSatisfactionDriver,
            (driver: any) => includes(selectedFeature, driver.sentiment)
        );
        const filteredDriverwiseReviewTrend = filter(
            driverwiseReviewTrend,
            (driver: any) => includes(selectedFeature, driver.theme)
        );

        return {
            customerSatisfactionDriver:
                formatCustomerSatisfactionData(filteredCsDrivers),
            driverwiseReviewTrend: formatDriverwiseReviewTrendData(
                filteredDriverwiseReviewTrend
                    ? groupByDateDriverwiseReviewTrendData(
                          filteredDriverwiseReviewTrend
                      )
                    : filteredDriverwiseReviewTrend
            ),
            csDriversTrendsFeatures: reviewsData?.csDriversTrendsFeatures,
        };
    }, [reviewsData, selectedFeature]);

    useEffect(() => {
        const { customerSatisfactionDriver } = reviewsData;
        customerSatisfactionDriver?.length &&
            setSelectedFeature(getTop5Sentiments(customerSatisfactionDriver));
    }, [reviewsData]);
    const onClickMoreHandler = (type: string) => {
        setShowAllReviewsWidget(true);
        setActiveReviewTab(type);
    };

    const onDriversFilterChange = (value: string[]) =>
        setSelectedDriver(
            includes(value, "all")
                ? reduce(
                      driverFilterOptions,
                      (acc, option) => {
                          if (option.value !== "all") acc.push(option.value);
                          return acc;
                      },
                      []
                  )
                : value
        );

    const onChannelComparisionChange = (value: string[]) =>
        setSelectedComparisionChannel(value);

    const onDriverClickHandler = (type: string, driver: string) => {
        setSelectedDriver([driver]);
        setReviewDrawerTitleState({
            type: "satisfaction-driver",
            payload: driver,
        });
        setActiveDriversTab(type);
        setShowSatisfactionDriversWidget(true);
    };

    const onProductRatingVsCompetitorsFilterChange = (value: string[]) =>
        setSelectedRatingFilter(
            includes(value, "all")
                ? reduce(
                      productRatingVsCompetitorsFilterOptions,
                      (acc, option) => {
                          if (option.value !== "all") acc.push(option.value);
                          return acc;
                      },
                      []
                  )
                : value
        );

    return (
        <WidgetModuleLayout
            loaderCondition={
                keys(reviewsData).length === 0 ||
                loadingState?.[ProductDeepdiveModules.RATINGS_REVIEWS]
            }
        >
            <SummaryWidget
                widgetData={reviewsData?.ratingSummaryData}
                activeTab="rating-reviews"
                onClickMoreHandler={onClickMoreHandler}
            />
            <SatisfactionDriversSummaryWidget
                moduleType="rating-reviews"
                widgetData={reviewsData?.satisfactionDriversSummaryData}
                onDriverClickHandler={onDriverClickHandler}
            />
            <TrendsWithCompareModalWidget
                widgetData={reviewsData?.ratingsReviewsTrend}
                graphAttributes={{
                    yAxisTitle: "Reviews",
                    yAxisOppositeTitle: "Rating",
                    yAxisOppositeTitleOptional: "Review Rating",
                }}
                widgetLinkName="Cross Channel"
                onWidgetLinkHandler={() =>
                    setShowCrossChannelRatingAndReviewWidget(true)
                }
            />
            <CsAndReviewTrendGroupedWidget
                type="skus"
                {...{
                    selectedFeature,
                    setSelectedFeature,
                    reviewsDataFormatted,
                }}
            />
            <ProductRatingVsCompetitorsWidget
                widgetData={productRatingVsCompetitorsData}
                {...{
                    selectedRatingFilter,
                    onProductRatingVsCompetitorsFilterChange,
                    productRatingVsCompetitorsFilterOptions,
                }}
            />
            <Row className="my-4" justify="center" align="middle" gutter={16}>
                <Col span={12}>
                    <WordCloudsWidget
                        title="Positive Reviews"
                        type={SentimentType.POSITIVE}
                        widgetData={reviewsData?.positiveReviewData}
                        onClickMoreHandler={onClickMoreHandler}
                    />
                </Col>
                <Col span={12}>
                    <WordCloudsWidget
                        title="Negative Reviews"
                        type={SentimentType.NEGATIVE}
                        widgetData={reviewsData?.negativeReviewData}
                        onClickMoreHandler={onClickMoreHandler}
                    />
                </Col>
            </Row>
            {showAllReviewsWidget && (
                <AllReviewsWidget
                    title={reviewDrawerTitleState["all-review"]}
                    show={showAllReviewsWidget}
                    setShow={setShowAllReviewsWidget}
                    widgetData={allReviewWidgetData}
                    {...{ activeReviewTab, setActiveReviewTab }}
                />
            )}
            {showSatisfactionDriversWidget && (
                <SatisfactionDriversWidget
                    title={reviewDrawerTitleState["satisfaction-driver"]}
                    show={showSatisfactionDriversWidget}
                    setShow={setShowSatisfactionDriversWidget}
                    driverData={reviewsData?.satisfactionDriversSummaryData}
                    widgetData={satisfactionDriverWidgetData}
                    driverFilterOptions={driverFilterOptions}
                    {...{
                        activeDriversTab,
                        setActiveDriversTab,
                        selectedDriver,
                        setSelectedDriver,
                        onDriversFilterChange,
                    }}
                />
            )}
            {showCrossChannelRatingAndReviewWidget && (
                <CrossChannelRatingAndReviewWidget
                    title={reviewDrawerTitleState["cross-channel"]}
                    show={showCrossChannelRatingAndReviewWidget}
                    setShow={setShowCrossChannelRatingAndReviewWidget}
                    widgetData={crossChannelWidgetData}
                    {...{
                        selectedComparisionChannel,
                        setSelectedComparisionChannel,
                        comparisionSelectOptions,
                        onChannelComparisionChange,
                        comparisionSelectDataType,
                        setComparisionSelectDataType,
                    }}
                />
            )}
        </WidgetModuleLayout>
    );
};

export default ProductAnalysisRatingsAndReviewsModule;
